import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Type, Check, Pencil } from "lucide-react";
import { adminApi, getToken, apiError } from "@/lib/adminApi";
import { setTextOverride } from "@/data/siteText";
import { assignAutoSlots, applyAutoText, autoTextEnabled } from "@/data/autoText";

const KEY = "ag_edit_text";

// Public: applies saved auto-text overrides to the DOM for every visitor.
export function AutoText() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!autoTextEnabled(pathname)) return;
    let cancelled = false;
    const run = () => { if (!cancelled) applyAutoText(pathname); };
    const timers = [150, 500, 1200, 2500, 4000].map((t) => setTimeout(run, t));
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [pathname]);
  return null;
}

export function TextEditor() {
  const loc = useLocation();
  const isAdmin = !!getToken();
  const hidden = loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/checkout");
  const [on, setOn] = useState(() => localStorage.getItem(KEY) === "1");
  const editing = useRef(null);

  const setEditable = useCallback((enable) => {
    document.querySelectorAll("[data-text-slot]").forEach((el) => {
      if (enable) { el.setAttribute("contenteditable", "true"); el.classList.add("ag-text-editable"); }
      else { el.removeAttribute("contenteditable"); el.classList.remove("ag-text-editable"); }
    });
  }, []);

  useEffect(() => {
    if (!on || hidden || !isAdmin) { setEditable(false); return; }
    assignAutoSlots(loc.pathname);
    setEditable(true);
    const t = setInterval(() => { assignAutoSlots(loc.pathname); setEditable(true); }, 800);
    return () => { clearInterval(t); setEditable(false); };
  }, [on, hidden, isAdmin, setEditable, loc.pathname]);

  useEffect(() => {
    if (!on || hidden || !isAdmin) return;
    const onFocusIn = (e) => {
      const el = e.target.closest?.("[data-text-slot]");
      if (!el) return;
      editing.current = { el, slot: el.getAttribute("data-text-slot"), original: el.innerText, cancelled: false };
    };
    const onKeyDown = (e) => {
      const cur = editing.current; if (!cur || e.target !== cur.el) return;
      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); cur.el.blur(); }
      else if (e.key === "Escape") { cur.cancelled = true; cur.el.innerText = cur.original; cur.el.blur(); }
    };
    const onFocusOut = async (e) => {
      const cur = editing.current; if (!cur || e.target !== cur.el) return;
      editing.current = null;
      const value = cur.el.innerText.trim();
      if (cur.cancelled || value === cur.original.trim()) return;
      try {
        await adminApi.put(`/admin/text/${cur.slot}`, { value });
        setTextOverride(cur.slot, value);
        toast.success("Text updated");
      } catch (err) {
        cur.el.innerText = cur.original;
        toast.error(apiError(err.response?.data?.detail) || "Could not save text.");
      }
    };
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("focusout", onFocusOut, true);
    return () => {
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("focusout", onFocusOut, true);
    };
  }, [on, hidden, isAdmin]);

  const toggle = () => setOn((v) => { const n = !v; localStorage.setItem(KEY, n ? "1" : "0"); return n; });

  if (hidden || !isAdmin) return null;

  return createPortal(
    <>
      <style>{`.ag-text-editable{outline:1px dashed #F4B999;outline-offset:3px;border-radius:2px;cursor:text;transition:background .15s;}
      .ag-text-editable:hover{background:rgba(251,196,168,0.14);}
      .ag-text-editable:focus{outline:2px solid #E97C6A;background:rgba(251,196,168,0.10);}`}</style>
      <button onClick={toggle} data-testid="text-editor-toggle"
        className={`fixed bottom-20 right-5 z-[9998] flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-semibold shadow-[0_12px_30px_-10px_rgba(0,0,0,0.5)] transition-all hover:scale-105 ${on ? "bg-[#2C2B2C] text-white" : "bg-white text-[#2C2B2C] ring-1 ring-[#E5DBC8]"}`}>
        {on ? <><Check className="h-4 w-4 text-[#FBC4A8]" /> Editing text</> : <><Type className="h-4 w-4 text-[#E97C6A]" /> Edit text</>}
      </button>
      {on && (
        <div data-testid="text-editor-banner"
          className="fixed bottom-20 left-5 z-[9998] flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-xs text-[#6E6168] shadow-[0_12px_30px_-12px_rgba(0,0,0,0.4)] ring-1 ring-[#E5DBC8]">
          <Pencil className="h-3.5 w-3.5 text-[#E97C6A]" /> Click any dashed text · Enter to save · Esc to cancel
        </div>
      )}
    </>,
    document.body
  );
}
